import React, { useEffect, useState } from 'react';
import { authHeaders } from './apiAuth';
import { textesDesactives, reactiverTextes } from './texteReglage';

// « Vos mots » — les trois listes que le SRS sait déjà tenir, et de quoi rejouer
// n'importe laquelle en une série.
//
// Rien n'est stocké pour cet écran : les trois listes se dérivent de
// `user_progress`, la table qui décide déjà de tout le reste. C'est la raison
// pour laquelle elles ne peuvent pas mentir, et pourquoi elles s'affichent aussi
// SANS compte : une progression anonyme est une vraie progression.
//
// Lancer une série n'invente pas de mode : c'est la recette `include` du quiz,
// la même que la CTA d'une fiche /mot/…. Le quiz n'est pas modifié, le SRS
// enregistre les réponses comme d'habitude, et la série suivante redevient
// normale d'elle-même.

const ONGLETS = ['a_revoir', 'erreurs', 'appris'];

// Combien de mots on montre. Il n'y a PAS d'ascenseur dans cette liste : deux
// zones de défilement imbriquées dans une même modale se disputent la molette et
// se voient toutes les deux. Une tranche courte suivie de « et N de plus » dit la
// même chose sans rien ajouter à l'écran.
const AFFICHES = 8;

// Une série fait dix questions au plus. En dessous, on ne complète pas : qui
// demande à revoir ses deux erreurs veut revoir ses deux erreurs, pas huit mots
// pris ailleurs pour faire nombre. La série fait alors deux questions.
const PAR_SERIE = 10;

// Deux, parce qu'à un seul mot le QCM n'a plus de leurre : ses options SONT les
// autres mots de la série, et il ne resterait qu'un bouton, qui est la réponse.
const MIN_SERIE = 2;

export default function MesMots({ userId, apiLang, lang, onStartSerie }) {
  const t = lang.ui.account.mots;
  const [data, setData] = useState(null);
  const [onglet, setOnglet] = useState(null);
  const [textesOff, setTextesOff] = useState(() => textesDesactives());

  useEffect(() => {
    if (!userId) return;
    let annule = false;
    fetch(`/api/mes_mots/${userId}/${apiLang}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (annule || !d?.listes) return;
        setData(d);
        // On ouvre sur la première liste NON VIDE, dans l'ordre de l'urgence :
        // ce qu'il faut revoir, puis ce qui a coincé, puis ce qui est acquis.
        // Ouvrir sur un onglet vide donnerait l'impression qu'il n'y a rien.
        setOnglet(ONGLETS.find((k) => (d.totaux?.[k] || 0) > 0) || null);
      })
      .catch(() => {});
    return () => { annule = true; };
  }, [userId, apiLang]);

  const total = ONGLETS.reduce((n, k) => n + (data?.totaux?.[k] || 0), 0);

  // Rien à montrer tant que personne n'a répondu à une seule question : le
  // panneau reste aussi léger qu'avant pour qui arrive.
  const reglageTextes = textesOff && (
    <div className="mb-4 pb-3 border-b border-slate-700/60 flex items-center justify-between gap-2 text-[11px]">
      <span className="text-slate-500">{lang.ui.account.textesOff}</span>
      <button
        onClick={() => { reactiverTextes(); setTextesOff(false); }}
        className="text-blue-400 hover:text-blue-300 transition-colors shrink-0"
      >
        {lang.ui.account.textesOn}
      </button>
    </div>
  );

  if (!data || !total) return reglageTextes || null;

  const liste = onglet ? (data.listes[onglet] || []) : [];
  const compte = onglet ? (data.totaux[onglet] || 0) : 0;
  // La liste ENTIÈRE part au back, pas les dix premiers : quand elle est plus
  // longue qu'une série, c'est le texte de fin qui choisit lesquels travailler.
  const ids = liste.map((w) => w.id);
  const taille = Math.min(PAR_SERIE, ids.length);

  return (
    <>
      {reglageTextes}
      <div className="mb-4 pb-4 border-b border-slate-700/60">
        <div className="flex items-baseline justify-between gap-2 mb-2">
          <h3 className="text-sm font-semibold text-slate-200">{t.title}</h3>
        </div>

        {/* Les trois onglets portent leur compte : c'est le chiffre qui décide
            lequel on ouvre, pas le libellé. Un onglet vide reste cliquable et
            dit pourquoi il est vide. */}
        <div className="flex gap-3 mb-2 text-xs border-b border-slate-700/60">
          {ONGLETS.map((k) => (
            <button
              key={k}
              onClick={() => setOnglet(k)}
              className={`pb-1.5 -mb-px border-b-2 transition-colors ${
                onglet === k
                  ? 'border-blue-400 text-blue-400 font-semibold'
                  : 'border-transparent text-slate-500 hover:text-slate-300'
              }`}
            >
              {t.tabs[k]}
              <span className="ml-1 tabular-nums opacity-70">{data.totaux[k] || 0}</span>
            </button>
          ))}
        </div>

        {onglet && (
          <>
            <p className="text-[11px] text-slate-500 leading-snug mb-2">{t.hints[onglet]}</p>

            {liste.length === 0 ? (
              <p className="text-xs text-slate-600 py-2">{t.empty[onglet]}</p>
            ) : (
              <>
                <ul className="flex flex-col gap-0.5 mb-2">
                  {liste.slice(0, AFFICHES).map((w) => (
                    <li key={w.id} className="flex items-baseline justify-between gap-3 text-xs">
                      <span lang="ru" className="text-blue-300 shrink-0">{w.russe}</span>
                      <span className="text-slate-500 text-right truncate">{w[lang.wordField]}</span>
                    </li>
                  ))}
                </ul>
                {compte > AFFICHES && (
                  <p className="text-[11px] text-slate-600 mb-2">{t.more(compte - AFFICHES)}</p>
                )}
                {ids.length >= MIN_SERIE ? (
                  <button
                    onClick={() => onStartSerie(ids)}
                    className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded py-2 text-xs font-semibold transition-colors"
                  >
                    {t.quiz(taille)}
                  </button>
                ) : (
                  <p className="text-[11px] text-slate-600 leading-snug">{t.tropPeu(MIN_SERIE)}</p>
                )}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}
